import { useSelector } from 'react-redux';
import { FormControl } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import FormLabelControl from '@material-ui/core/FormControlLabel';
import {
  BaseClickable,
  Box,
  Button,
  palette,
  Stack,
  style,
  TableSection,
  Text,
  Checkbox,
} from '@solvhealth/jigsaw-v1';
// @ts-ignore FIXME: Try `npm install @types/rc-form` if it exists or a... Remove this comment to see the full error message
import { createForm, formShape } from 'rc-form';
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import LocationInterface from '@solvhealth/types/interfaces/Location';
import TextField from '../../../../components/SolvPatternLibrary/TextField';
import { fontFamily } from '../../../../constants/text';
import history from '../../../../core/history';
import { isNativeApp } from '../../../../core/util/device';
import { NativeFunctions } from '../../../../core/util/native';
import {
  PaperworkSectionRoute,
  ProviderGroupPaperworkSectionRoute,
} from '../../paperworkConstants';
import PaperworkLayout from '../../PaperworkLayout';
import { paperworkHasConsentForms } from '../../util';
import DocumentIcon from '../Icons/DocumentIcon';
import SignHereIcon from '../Icons/SignHereIcon';
import { isProviderGroupLocation } from '~/core/util/location';
import useSetTitle from '~/hooks/useSetTitle';
import { PGSectionNumber } from '../../providerGroups/constants';
import { SectionNumber } from '../../constants';
import { useSetupSection } from '../../hooks/useSetupSection';
import { usePaperworkContextBookingId } from '~/routes/paperwork/PaperworkBookingIdContext';
import InvalidPhoneNumber from '../../InvalidPhoneNumber';

const DocumentLink = styled(BaseClickable).withConfig({ componentId: 'sc-ihnvcb-0' })(
  style({
    py: 3,
    pr: 2,
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
  })
);

const SignatureLine = styled(TextField).withConfig({ componentId: 'sc-ihnvcb-1' })`
  flex-grow: 1;

  div {
    border: none !important;

    &:before {
      border: none !important;
    }

    &:after {
      border: none !important;
    }
  }

  input {
    font-family: 'Homemade Apple', cursive !important;

    &::placeholder {
      color: ${palette.gray[400]};
      font-family: ${fontFamily.book};
      opacity: 1;
    }
  }
`;

const StyledFormLabelControl = styled(FormLabelControl).withConfig({ componentId: 'sc-ihnvcb-2' })`
  align-items: flex-start;
`;

const StyledCheckbox = styled(Checkbox).withConfig({ componentId: 'sc-ihnvcb-3' })`
  margin: 0 12px;
`;

const ESIGN = 'eSignConsent';
const SIGNATURE = 'signature';

type Props = {
  createConsentFormSignatures?: (...a: any) => any;
  form: formShape;
};

const PaperworkConsent = (props: Props) => {
  const { createConsentFormSignatures, form } = props;

  const bookingId = usePaperworkContextBookingId();
  const location = useSelector((state) => state.location as LocationInterface);
  const paperwork = useSelector((state) => state.paperwork);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation(['paperwork', 'common']);
  const title = t('paperwork:consent.htmlTitle');
  const isProviderGroup = isProviderGroupLocation(location);

  const { onGoForward, onGoBack, doesAccountMatchBooking } = useSetupSection(
    isProviderGroup ? PGSectionNumber.Consent : SectionNumber.Consent,
    isProviderGroup ? PGSectionNumber : SectionNumber
  );

  useSetTitle(title);

  const canSubmit = () => {
    const { getFieldValue } = form;

    return getFieldValue(SIGNATURE) && getFieldValue(ESIGN) && paperworkHasConsentForms(paperwork);
  };

  const handleDocumentLinkOnClick = (link: any) => () => {
    if (isNativeApp()) {
      const openLink = NativeFunctions.openNativeUrl;
      if (openLink.exists) openLink.call(link);
      // eslint-disable-next-line no-alert
      else alert(t('paperwork:consent.updateToView'));
    } else {
      window.open(link, '_blank');
    }
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    setIsSubmitting(true);

    const locationConsentFormIds = paperwork?.consentForms?.forms?.map((form: any) => form.id);
    const signature = form.getFieldValue(SIGNATURE);

    const onSuccess = () => onGoForward();

    createConsentFormSignatures?.(bookingId, locationConsentFormIds, signature, onSuccess);
  };

  const renderConsentFormLinks = () =>
    paperwork?.consentForms?.forms?.map((form: any) => (
      <Box key={form.display_name} pl={3}>
        <Box pr={3}>
          <DocumentLink
            onClick={handleDocumentLinkOnClick(
              `https://${form.aws_bucket_name}.s3.amazonaws.com/${form.aws_path}`
            )}
          >
            <Stack
              flex={1}
              justifyContent="flex-start"
              row
              space={1}
              textColor="gray-500"
              width="100%"
            >
              <DocumentIcon />
              <Text>{form.display_name}</Text>
            </Stack>
          </DocumentLink>
        </Box>
        <Box bg="gray-200" height="1px" width="100%" />
      </Box>
    ));

  const renderErrorDialog = () => {
    const finalizeRoute = isProviderGroup
      ? ProviderGroupPaperworkSectionRoute.FINALIZE
      : PaperworkSectionRoute.FINALIZE;
    if (paperwork?.signatures?.error) {
      return (
        <Dialog open>
          <DialogContent>
            <DialogContentText>{t('paperwork:consent.error.message')}</DialogContentText>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => history.push(`${finalizeRoute}/${bookingId}`)}>
              {t('common:finish')}
            </Button>
          </DialogActions>
        </Dialog>
      );
    }

    return null;
  };

  const renderForm = () => {
    const { getFieldDecorator, getFieldValue } = form;

    return (
      <Stack as="form" onSubmit={handleSubmit} space={3}>
        <Box px={3}>
          <Stack
            alignItems="center"
            borderRadius="base"
            row
            sx={{
              transition: 'box-shadow .15s',
              ':focus-within': { borderColor: 'gray-600', boxShadow: 'focus' },
            }}
          >
            <Box
              alignItems="center"
              borderBottom="dashed 2px"
              borderColor="gray-400"
              display="flex"
              flex="1"
            >
              <SignHereIcon />
              {getFieldDecorator(SIGNATURE)(
                <SignatureLine placeholder={t('paperwork:consent.addSignature')} />
              )}
            </Box>
          </Stack>
        </Box>

        <Stack px={3} space={3}>
          <FormControl>
            <StyledFormLabelControl
              control={getFieldDecorator(ESIGN)(
                <StyledCheckbox
                  checked={getFieldValue(ESIGN)}
                  data-test-id="consent-checkbox"
                  size="medium"
                  tone="whiteLabel"
                />
              )}
              label={<Text variant="body2">{t('paperwork:consent.agreementCopy')}</Text>}
            />
          </FormControl>
          <Button
            data-test-id="consent-submit-button"
            disabled={!canSubmit()}
            fullWidth
            loading={isSubmitting}
            type="submit"
          >
            {t('common:continue.capitalize')}
          </Button>
        </Stack>
      </Stack>
    );
  };

  if (!doesAccountMatchBooking) {
    return <InvalidPhoneNumber />;
  }

  if (paperworkHasConsentForms(paperwork)) {
    return (
      <PaperworkLayout
        noPadding
        onGoBack={onGoBack}
        section={isProviderGroup ? PGSectionNumber.VerificationAndReview : SectionNumber.Consent}
        subtitle={t('paperwork:consent.subtitle')}
        table
        title={t('paperwork:consent.title')}
      >
        <Helmet>
          <link
            href="https://fonts.googleapis.com/css?family=Homemade+Apple&display=swap"
            rel="stylesheet"
          />
        </Helmet>

        <TableSection>
          <Stack space={3}>
            <Stack>{renderConsentFormLinks()}</Stack>
            <Stack>{renderForm()}</Stack>
          </Stack>
        </TableSection>
        {renderErrorDialog()}
      </PaperworkLayout>
    );
  }

  return <div />;
};

export default createForm()(PaperworkConsent);
